import { makeObservable, observable, action, runInAction } from "mobx";
import * as api from "../lib/customerFilesApi";

// Backoff schedule (ms) for the post-upload scan poll. Capped at 5s — the scan
// (a 4 KB magic-byte sniff) is normally done within a few seconds.
function _pollDelayMs(i) {
  return Math.min(800 * Math.pow(1.6, i), 5000);
}


export class CustomerFilesStore {
  files = new Map();
  programId = null;
  projectId = null;
  nextCursor = null;
  loading = false;
  error = null;
  // Chat-scoped folders + membership (A3).
  chatFolders = [];
  chatFiles = [];
  chatLoading = false;
  // Tier-membership (promote) view: files promoted to the active project/program scope.
  promoted = [];
  promotedLoading = false;
  // User-facing upload error (e.g. session expired on init-upload). Surfaced
  // in the Data drawer instead of being swallowed to the console.
  uploadError = null;
  // When true, include kind=code files in file-list responses (default hidden).
  showCode = false;

  constructor({ programId = null, projectId = null } = {}) {
    this.programId = programId;
    this.projectId = projectId;
    makeObservable(this, {
      files: observable,
      programId: observable,
      projectId: observable,
      nextCursor: observable,
      loading: observable,
      error: observable,
      chatFolders: observable,
      chatFiles: observable,
      chatLoading: observable,
      promoted: observable,
      promotedLoading: observable,
      loadFiles: action,
      loadMore: action,
      applyChange: action,
      updateUploadProgress: action,
      rename: action,
      delete: action,
      archiveFile: action,
      restoreFile: action,
      switchProject: action,
      setProgram: action,
      loadChat: action,
      createFolder: action,
      renameFolder: action,
      archiveFolder: action,
      assignFile: action,
      removeFromChat: action,
      loadPromoted: action,
      promote: action,
      demote: action,
      onUploadComplete: action,
      uploadError: observable,
      setUploadError: action,
      clearUploadError: action,
      showCode: observable,
      setShowCode: action,
    });
  }

  setUploadError(message) { this.uploadError = message || null; }
  clearUploadError() { this.uploadError = null; }
  setShowCode(v) { this.showCode = !!v; }

  /**
   * Called when the upload SW broadcasts upload_complete. A freshly uploaded
   * file isn't in the store yet (nothing adds it during upload, and the SSE
   * change channel isn't wired), so updateUploadProgress would no-op and the
   * file wouldn't appear until a manual reload. Fetch its metadata and upsert
   * it into the current project's list so it shows immediately.
   *
   * complete-upload leaves the file in the `scanning` state; the scanner Lambda
   * then flips DDB to `ready`/`failed` but emits no live event we can hear (it
   * runs off-host and can't reach the unix-socket SSE Redis). So we poll
   * getFileMeta until the file settles, applying each result — otherwise the
   * row sticks on the "🛡 scanning" badge until a manual reload. Polling stops
   * on a terminal status, after maxAttempts, on a fetch error, or if the user
   * navigates to a different project mid-scan.
   */
  async onUploadComplete(fileId, { sleep = (ms) => new Promise((r) => setTimeout(r, ms)), maxAttempts = 8 } = {}) {
    if (!fileId || !this.programId || !this.projectId) return;
    const programId = this.programId;
    const projectId = this.projectId;

    // Guard against a project switch mid-scan — only upsert if the file still
    // belongs to the project in view. Returns false once the project changed.
    const upsert = (meta) => {
      if (this.programId !== programId || this.projectId !== projectId) return false;
      if (meta && meta.file_id) {
        this.files.set(meta.file_id, { ...this.files.get(meta.file_id), ...meta });
        this.uploadError = null; // a success supersedes any prior upload error
      }
      return true;
    };

    let attempt = 0;
    while (attempt < maxAttempts) {
      if (attempt > 0) {
        if (this.programId !== programId || this.projectId !== projectId) return;
        await sleep(_pollDelayMs(attempt - 1));
        if (this.programId !== programId || this.projectId !== projectId) return;
      }
      attempt += 1;
      let meta;
      try {
        meta = await api.getFileMeta(programId, projectId, fileId);
      } catch (_) {
        // File not in the current project (user navigated away) or a transient
        // error — it'll appear on the next loadFiles for that project.
        return;
      }
      if (!runInAction(() => upsert(meta))) return;
      if (!meta || meta.status !== "scanning") return; // settled (ready/failed)
    }
  }

  // ---- chat folders + membership ----
  folderOf(fileId) {
    const m = this.chatFiles.find((f) => f.file_id === fileId);
    return m ? m.folder_name || null : null;
  }

  async loadChat(chatId) {
    if (!chatId) return;
    this.chatLoading = true;
    try {
      const [folders, files] = await Promise.all([
        api.listChatFolders(chatId),
        api.listChatFiles(chatId, { include: "all", include_code: this.showCode }),
      ]);
      runInAction(() => {
        this.chatFolders = folders || [];
        this.chatFiles = files || [];
        this.chatLoading = false;
      });
    } catch (e) {
      runInAction(() => { this.error = e; this.chatLoading = false; });
    }
  }

  async createFolder(chatId, name) {
    await api.createChatFolder(chatId, name);
    await this.loadChat(chatId);
  }
  async renameFolder(chatId, oldName, newName) {
    await api.renameChatFolder(chatId, oldName, newName);
    await this.loadChat(chatId);
  }
  async archiveFolder(chatId, name) {
    const result = await api.archiveChatFolder(chatId, name);
    await this.loadChat(chatId);
    await this.loadFiles(this.programId, this.projectId, { include: "all" });
    return result;
  }
  async assignFile(chatId, fileId, folderName) {
    await api.assignChatFile(chatId, fileId, folderName);
    await this.loadChat(chatId);
  }
  async removeFromChat(chatId, fileId) {
    await api.removeChatFile(chatId, fileId);
    await this.loadChat(chatId);
  }

  async loadPromoted(tier, scopeId, { include = "ready" } = {}) {
    this.promotedLoading = true;
    this.error = null;
    try {
      const resp = await api.listPromoted(tier, scopeId, { include });
      runInAction(() => { this.promoted = resp.files || []; this.promotedLoading = false; });
    } catch (e) {
      runInAction(() => { this.error = e; this.promotedLoading = false; });
      throw e;
    }
  }

  // tier: "project" | "program". Returns the promotion record; throws on 403 (no authority).
  async promote(programId, projectId, fileId, tier, { destFolder = null, sourceChatId = null } = {}) {
    return api.promoteFile(programId, projectId, fileId, { tier, dest_folder: destFolder, source_chat_id: sourceChatId });
  }

  async demote(programId, projectId, fileId, tier) {
    return api.demoteFile(programId, projectId, fileId, tier);
  }

  /**
   * Wire the active (programId, projectId) pair. Components call this on
   * navigation so the singleton store reflects the URL.
   */
  setProgram(programId, projectId) {
    const programChanged = programId !== this.programId;
    const projectChanged = projectId !== this.projectId;
    if (!programChanged && !projectChanged) return;
    this.programId = programId;
    this.projectId = projectId;
    this.files.clear();
    this.nextCursor = null;
  }

  async loadFiles(programId, projectId, { include = "ready" } = {}) {
    this.loading = true;
    this.error = null;
    try {
      const resp = await api.listFiles(programId, projectId, { include, include_code: this.showCode });
      runInAction(() => {
        this.programId = programId;
        this.projectId = projectId;
        this.files.clear();
        for (const f of resp.files) this.files.set(f.file_id, f);
        this.nextCursor = resp.next_cursor;
        this.loading = false;
      });
    } catch (e) {
      // PLEX-4055: a 404 (project deleted / stale bookmark / bad URL) or any other
      // listFiles failure must not become an unhandled rejection at the fire-and-forget
      // caller (DataDrawer's load effect calls loadFiles without awaiting/catching).
      // Record it on `error` -- the drawer renders friendlyError(error), which maps
      // 404 -> "That item could not be found." -- and resolve instead of re-throwing.
      //
      // Also clear the file list (review, Tim): DataDrawer renders the file rows
      // unconditionally (only the "empty" placeholder is gated on !error), so on a
      // failed load after navigating project A -> B, B's error would show ABOVE A's
      // stale rows. Drop the previous project's data so the drawer shows only the error.
      runInAction(() => {
        this.error = e;
        this.loading = false;
        this.files.clear();
        this.nextCursor = null;
      });
    }
  }

  async loadMore(programId, projectId) {
    if (!this.nextCursor) return;
    // PLEX-4055: same as loadFiles -- record the error rather than letting a
    // rejected pagination fetch surface as an unhandled rejection.
    //
    // (review, Tim) nextCursor is intentionally left unchanged on failure so the
    // page stays retryable. To avoid a stale error surviving a later recovery, a
    // successful page clears `error`. No scroll handler calls loadMore yet, so the
    // "retry a failing endpoint repeatedly" case isn't reachable today; when a
    // caller is added it must gate on `error` before re-invoking.
    try {
      const resp = await api.listFiles(programId, projectId, { cursor: this.nextCursor });
      runInAction(() => {
        for (const f of resp.files) this.files.set(f.file_id, f);
        this.nextCursor = resp.next_cursor;
        this.error = null;
      });
    } catch (e) {
      runInAction(() => { this.error = e; });
    }
  }

  switchProject(programId, projectId) {
    if (programId === this.programId && projectId === this.projectId) return;
    this.programId = programId;
    this.projectId = projectId;
    this.files.clear();
    this.nextCursor = null;
  }

  applyChange(projectId, payload) {
    if (projectId && projectId !== this.projectId) return;
    const { file_id, action: a, status, name, ...extra } = payload;
    if (a === "deleted") {
      this.files.delete(file_id);
      return;
    }
    const existing = this.files.get(file_id) || { file_id };
    const next = { ...existing, ...extra };
    if (status !== undefined) next.status = status;
    if (name !== undefined) next.name = name;
    this.files.set(file_id, next);
  }

  updateUploadProgress(fileId, progress) {
    const existing = this.files.get(fileId);
    if (!existing) return;
    this.files.set(fileId, { ...existing, progress });
  }

  async rename(programId, projectId, fileId, name) {
    const updated = await api.renameFile(programId, projectId, fileId, name);
    runInAction(() => {
      const existing = this.files.get(fileId);
      if (existing) this.files.set(fileId, { ...existing, ...updated });
    });
  }

  async delete(programId, projectId, fileId) {
    await api.deleteFile(programId, projectId, fileId);
    runInAction(() => { this.files.delete(fileId); });
  }

  // Soft-archive / restore. The endpoints return the updated file, so merge it
  // in (status flips to archived/ready) rather than dropping the row — the row
  // stays in the map so the "Show archived" view and Restore keep working.
  async archiveFile(programId, projectId, fileId) {
    const updated = await api.archiveFile(programId, projectId, fileId);
    runInAction(() => {
      const existing = this.files.get(fileId);
      if (existing) this.files.set(fileId, { ...existing, ...updated });
    });
    return updated;
  }

  async restoreFile(programId, projectId, fileId) {
    const updated = await api.restoreFile(programId, projectId, fileId);
    runInAction(() => {
      const existing = this.files.get(fileId);
      if (existing) this.files.set(fileId, { ...existing, ...updated });
    });
    return updated;
  }

  /**
   * Subscribe to backend SSE changes for this project.
   *
   * The backend continues to emit the legacy `project_files.{project_id}.changed`
   * channel name during the customer-files transition — do not rename here.
   */
  subscribeToChanges(projectId, sseClient) {
    const channel = `project_files.${projectId}.changed`;
    return sseClient.subscribe(channel, (payload) => this.applyChange(projectId, payload));
  }
}
